// "Popular right now": what's being opened most, with all-time open counts
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Flame } from 'lucide-react';
import { CATEGORIES } from '@/data';
import { ClipThumb } from '@/components/ClipThumb';
import type { Clip, ClipView } from '@/types';

const DAY = 24 * 60 * 60 * 1000;
const compact = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : String(n));

interface Ranked { clip: Clip; couples: number; opens: number; window: 'week' | 'month' | 'all' }

// Distinct couples who opened each clip, in the tightest window that has enough activity
export const rankPopular = (clips: Clip[], views: ClipView[], limit = 10): Ranked[] => {
  const byId = new Map(clips.map(c => [c.id, c]));
  const nowMs = Date.now();
  // Every open ever, by anyone (couples, reviewers, repeat watches) — the headline number
  const opens = new Map<string, number>();
  for (const v of views) opens.set(v.clipId, (opens.get(v.clipId) || 0) + 1);
  const totalOpens = (clip: Clip) => (opens.get(clip.id) || 0) + (clip.couplesTried || 0);
  for (const [window, days] of [['week', 7], ['month', 30]] as const) {
    const viewers = new Map<string, Set<string>>();
    for (const v of views) {
      if (v.source !== 'datenite' || !byId.has(v.clipId)) continue;
      if (nowMs - new Date(v.viewedAt).getTime() > days * DAY) continue;
      if (!viewers.has(v.clipId)) viewers.set(v.clipId, new Set());
      viewers.get(v.clipId)!.add(v.viewerId);
    }
    if (viewers.size >= 4) {
      return [...viewers.entries()]
        .map(([id, set]) => ({ clip: byId.get(id)!, couples: set.size, opens: totalOpens(byId.get(id)!), window }))
        .sort((a, b) => b.couples - a.couples || (b.clip.couplesTried || 0) - (a.clip.couplesTried || 0))
        .slice(0, limit);
    }
  }
  // Quiet spell: fall back to the all-time most tried
  return [...clips]
    .sort((a, b) => totalOpens(b) - totalOpens(a))
    .slice(0, limit)
    .map(clip => ({ clip, couples: clip.couplesTried || 0, opens: totalOpens(clip), window: 'all' as const }));
};

export const PopularNow: React.FC<{ clips: Clip[]; views: ClipView[]; onOpen: (clip: Clip) => void }> = ({ clips, views, onOpen }) => {
  const ranked = useMemo(() => rankPopular(clips, views), [clips, views]);
  const row = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; left: number; moved: boolean } | null>(null);
  const [edges, setEdges] = useState({ start: true, end: false });

  const updateEdges = () => {
    const el = row.current;
    if (el) setEdges({ start: el.scrollLeft <= 4, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 4 });
  };
  useEffect(updateEdges, [ranked]);

  // Arrows page by roughly one screen of cards
  const page = (dir: 1 | -1) => row.current?.scrollBy({ left: dir * row.current.clientWidth * 0.85, behavior: 'smooth' });

  // Mouse drag to scroll (touch already scrolls natively)
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse' || !row.current) return;
    drag.current = { x: e.clientX, left: row.current.scrollLeft, moved: false };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current || !row.current) return;
    const dx = e.clientX - drag.current.x;
    if (Math.abs(dx) > 5) drag.current.moved = true;
    row.current.scrollLeft = drag.current.left - dx;
  };
  const endDrag = () => { setTimeout(() => { drag.current = null; }, 0); };

  if (ranked.length === 0) return null;
  const windowLabel = ranked[0]!.window === 'week' ? 'this week' : ranked[0]!.window === 'month' ? 'this month' : 'all time';

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-[17px] font-bold flex items-center gap-2">
          <Flame className="w-5 h-5 text-attention" /> Popular right now
        </h2>
        <span className="flex items-center gap-2">
          <span className="text-[13px] text-ink-muted">Trending {windowLabel}</span>
          <span className="hidden sm:flex gap-1.5">
            <button onClick={() => page(-1)} disabled={edges.start} aria-label="Scroll back" className="w-9 h-9 rounded-full bg-surface border border-line text-ink-2 hover:text-ink flex items-center justify-center disabled:opacity-30">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => page(1)} disabled={edges.end} aria-label="Scroll forward" className="w-9 h-9 rounded-full bg-surface border border-line text-ink-2 hover:text-ink flex items-center justify-center disabled:opacity-30">
              <ChevronRight className="w-4 h-4" />
            </button>
          </span>
        </span>
      </div>
      <div
        ref={row}
        onScroll={updateEdges}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onClickCapture={e => { if (drag.current?.moved) { e.preventDefault(); e.stopPropagation(); } }}
        onDragStart={e => e.preventDefault()}
        className="flex gap-3 overflow-x-auto no-scrollbar -mx-5 px-5 lg:mx-0 lg:px-0 snap-x snap-mandatory pb-1 sm:snap-none sm:cursor-grab active:cursor-grabbing select-none"
      >
        {ranked.map(({ clip, opens }, i) => (
          <button
            key={clip.id}
            onClick={() => onOpen(clip)}
            className="rise-in group relative shrink-0 w-[46%] sm:w-[30%] lg:w-[23%] snap-start text-left"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <span className="relative block">
              <ClipThumb url={clip.link} className="w-full aspect-[3/4] h-auto rounded-[20px] group-hover:brightness-110 transition" />
              <span className="absolute inset-x-0 bottom-0 h-1/2 rounded-b-[20px] bg-gradient-to-t from-ground/95 to-transparent" />
              <span className="absolute left-2.5 bottom-1.5 font-display text-[54px] leading-none font-medium text-ink/90 drop-shadow">{i + 1}</span>
              {i < 3 && (
                <span className="absolute left-2 top-2 h-6 px-2 rounded-full bg-her text-ground text-[11px] font-extrabold uppercase tracking-wide flex items-center">{i === 0 ? 'Hottest' : 'Hot'}</span>
              )}
            </span>
            <span className="block mt-2 text-[14px] font-semibold leading-snug truncate">{clip.title}</span>
            <span className="flex items-center gap-1.5 text-[12px] text-ink-muted truncate">
              {opens > 0 && <span className="shrink-0 font-bold text-attention flex items-center gap-0.5"><Flame className="w-3 h-3" />{compact(opens)}</span>}
              <span className="truncate">{CATEGORIES.find(c => c.id === clip.categoryId)?.name}</span>
            </span>
          </button>
        ))}
      </div>
    </section>
  );
};
